/* eslint-disable import/no-extraneous-dependencies, max-len, no-plusplus */
import { Grid } from '@mui/material';
import { BasicButton } from '@tassat/button';
import { SecondaryApiTypeEnum, getCountryOptions, getStateProvinceOptions } from '@tassat/common-definitions';
import { CustomerModifyFirmReq as FredCustomerModifyFirmReq, FirmRecord as FredFirmRecord } from '@tassat/fred-definitions';
import { FirmRecord as PaymentsFirmRecord } from '@tassat/payments-definitions';
import {
    ActionTypes,
    SelectOption,
    doesWorkflowRejectCodeExist
} from '@tassat/ui-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    ConfirmationWorkflowState,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import { Formik, FormikProps } from 'formik';
import React from 'react';

import { FirmFormContents } from './FirmFormContents';
import { validateFirmForm } from './submission';
import { IFormProps, IFormValues, IFormikProps } from './types';

const FormContents = (props: IFormProps) => {
    const {
        actionType,
        editFirm,
        firmEoaAccounts,
        features,
        getTransactions,
        getCollateral,
        updateFirm,
        firmAssetStats,
        firmAssetMapping
    } = props;

    const [
        confirmationWorkflowState,
        dispatchConfirmationWorkflowAction
    ] = React.useReducer<
        React.Reducer<ConfirmationWorkflowState, ConfirmationWorkflowAction>
    >(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);

    const firmCurrentTickers: string[] = React.useMemo(() => {
        return (firmAssetMapping || []).map((asset) => asset.value as string);
    }, [firmAssetMapping]);

    const firmCurrentEntitledAssets = React.useMemo(() => {
        return firmCurrentTickers.map((ticker) => ({ EntitledAssetsTicker: ticker }));
    }, [firmCurrentTickers]);

    const confirmationContext = React.useMemo(() => ({
        confirmationWorkflowState,
        dispatchConfirmationWorkflowAction
    }), [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const getValidationError = async () => {
        const walletBalance = firmEoaAccounts.some((account) => (
            account.walletStats?.some((stats) => parseFloat(stats.CurrentPosition) > 0)
        ));

        if (walletBalance) {
            return 'Cannot close a firm that has accounts with positive balance';
        }

        // make history request to check on open transactions
        if (getTransactions) {
            try {
                const response = await getTransactions();
                if (response.data && response.data.TotalCount > 0) {
                    return 'Cannot close a firm with open transactions';
                }
            } catch (error) {
                return 'Could not validate if there are open transactions, please try again';
            }
        }

        if (getCollateral) {
            try {
                const response = await getCollateral();
                if (response.data && response.data.TotalCount > 0) {
                    return 'Cannot close firm due to pending pledges';
                }
            } catch (error) {
                return 'Could not validate if there are open transactions, please try again';
            }
        }

        return '';
    };

    const closeFirmValidation = async (firm: PaymentsFirmRecord | FredFirmRecord): Promise<boolean> => {
        if (firmAssetStats) {
            if (firmAssetStats.AvailablePosition > 0) {
                const validationErrorAction: ConfirmationWorkflowAction = {
                    type: 'VALIDATION_FAILED',
                    workflowConfiguration: {
                        validationTitle: `Cannot Close Firm ${firm.Name}`,
                        validationMessage: 'Cannot close firm with accounts containing a positive balance'
                    }
                };
                dispatchConfirmationWorkflowAction(validationErrorAction);

                return true;
            } else if (
                firmAssetStats.TodaysPendingBuyCount > 0 ||
                firmAssetStats.TodaysPendingRedemptionCount > 0 ||
                firmAssetStats.TodaysPendingSellCount > 0 ||
                firmAssetStats.TodaysPendingSendCount > 0 ||
                firmAssetStats.TodaysPendingSubscriptionCount > 0 ||
                firmAssetStats.TodaysPendingWithdrawalCount > 0
            ) {
                const validationErrorAction: ConfirmationWorkflowAction = {
                    type: 'VALIDATION_FAILED',
                    workflowConfiguration: {
                        validationTitle: `Cannot Close Firm ${firm.Name}`,
                        validationMessage: 'Cannot close firm with accounts that have pending transactions'
                    }
                };
                dispatchConfirmationWorkflowAction(validationErrorAction);

                return true;
            }
        }
        try {
            const error = await getValidationError();
            if (error) {
                const validationErrorAction: ConfirmationWorkflowAction = {
                    type: 'VALIDATION_FAILED',
                    workflowConfiguration: {
                        validationTitle: `Cannot Close Firm ${firm.Name}`,
                        validationMessage: error
                    }
                };
                dispatchConfirmationWorkflowAction(validationErrorAction);

                return true;
            }
        } catch (error) {
            const validationErrorAction: ConfirmationWorkflowAction = {
                type: 'VALIDATION_FAILED',
                workflowConfiguration: {
                    validationTitle: `Cannot Close Firm ${firm.Name}`,
                    validationMessage: error as string
                }
            };
            dispatchConfirmationWorkflowAction(validationErrorAction);
        }

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: firm,
                actionType: ActionTypes.close,
                confirmTitle: `Confirm Closing ${firm.Name}`,
                confirmBody: `Please confirm closing the firm ${firm.Name}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const closeFirm = async (firm: PaymentsFirmRecord | FredFirmRecord) => {
        try {
            const fredFirmRecord = firm as FredFirmRecord;
            const sendValues: Partial<FredCustomerModifyFirmReq> = {
                Name: fredFirmRecord.Name,
                FirmEmail: fredFirmRecord.Email,
                Notes: fredFirmRecord.Notes,
                IsTokenAPIFirm: fredFirmRecord.IsTokenAPIFirm,
                IsCollateral: fredFirmRecord.IsCollateral,
                IsClosed: true,
                Lei: fredFirmRecord.Lei,
                IsLiquidityFacility: fredFirmRecord.IsLiquidityFacility,
                CountryCode: fredFirmRecord.CountryCode,
                StateCode: fredFirmRecord.StateCode,
                EntitledAssets: firmCurrentEntitledAssets
            };

            const response = await updateFirm(firm.FirmId, sendValues);

            if (response.data?.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Closed Firm ${firm.Name}`,
                        successBody: `Successfully closed the firm ${firm.Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Close Firm ${firm.Name}`,
                        failureBody: `Could not close firm ${firm.Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Close firm ${firm.Name}`,
                    failureBody: `Something went wrong closing the firm ${firm.Name}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const reopenFirmValidation = (firm: PaymentsFirmRecord | FredFirmRecord): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: firm,
                actionType: ActionTypes.reopen,
                confirmTitle: `Confirm Reopening Firm ${firm.Name}`,
                confirmBody: `Please confirm reopening the firm ${firm.Name}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const reopenFirm = async (firm: PaymentsFirmRecord | FredFirmRecord) => {
        try {
            const fredFirmRecord = firm as FredFirmRecord;
            const sendValues: Partial<FredCustomerModifyFirmReq> = {
                Name: fredFirmRecord.Name,
                FirmEmail: fredFirmRecord.Email,
                Notes: fredFirmRecord.Notes,
                IsTokenAPIFirm: fredFirmRecord.IsTokenAPIFirm,
                IsCollateral: fredFirmRecord.IsCollateral,
                IsClosed: false,
                Lei: fredFirmRecord.Lei,
                IsLiquidityFacility: fredFirmRecord.IsLiquidityFacility,
                CountryCode: fredFirmRecord.CountryCode,
                StateCode: fredFirmRecord.StateCode,
                EntitledAssets: firmCurrentEntitledAssets
            };

            const response = await updateFirm(firm.FirmId, sendValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Reopened Firm ${firm.Name}`,
                        successBody: `Successfully reopened the firm ${firm.Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Reopen Firm ${firm.Name}`,
                        failureBody: `Could not reopen firm ${firm.Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Reopen Firm ${firm.Name}`,
                    failureBody: `Something went wrong reopening the firm ${firm.Name}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const renderButton = () => {
        if (actionType === ActionTypes.add || (actionType === ActionTypes.edit && !editFirm)) {
            return null;
        }

        return (
            actionType === ActionTypes.edit && !editFirm?.IsClosed ? (
                <Grid item>
                    <BasicButton
                        onClick={() => editFirm && closeFirmValidation(editFirm)}
                        text='Close Firm'
                        type='action'
                    />
                </Grid>
            ) : (
                <Grid item>
                    <BasicButton
                        onClick={() => editFirm && reopenFirmValidation(editFirm)}
                        text='Reopen Firm'
                        type='action'
                    />
                </Grid>
            )
        );
    };

    const renderContents = () => {
        return (
            <FirmFormContents
                renderButton={renderButton}
                {...props}
            />
        );
    };

    React.useEffect(() => {
        // set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: editFirm,
                setSelectedItem: () => editFirm,
                preActionValidationAndConfig: {
                    [ActionTypes.close]: closeFirmValidation,
                    [ActionTypes.reopen]: reopenFirmValidation
                },
                onConfirm: {
                    [ActionTypes.close]: closeFirm,
                    [ActionTypes.reopen]: reopenFirm
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, [editFirm]);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            {renderContents()}
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
};

export default function FirmForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        actionType = ActionTypes.add,
        editFirm,
        firms,
        firmUsers,
        firmEoaAccounts,
        userAccounts,
        features,
        submitFirmForm,
        getTransactions,
        getCollateral,
        updateFirm,
        availableAssets,
        firmAssetEntitlements,
        firmAssetStats,
        firmSecondaryApiConnections,
        secondaryApiConnectionExists,
        firmAllApiTypeUsers
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');

    const availableAssetsMapped: SelectOption[] = React.useMemo(() => {
        return availableAssets ?
            availableAssets
                .filter((asset) => asset.IsEnabled)
                .map((asset) => (
                    {
                        label: asset.Ticker,
                        value: asset.Ticker
                    }
                ))
            : [];
    }, [availableAssets]);

    const firmAssetMapping: SelectOption[] = React.useMemo(() => {
        return firmAssetEntitlements ?
            firmAssetEntitlements
                .map((entitlement) => (
                    {
                        label: entitlement.Ticker,
                        value: entitlement.Ticker
                    }
                ))
            : [];
    }, [firmAssetEntitlements]);

    let initialValues = {} as IFormValues;
    if (actionType === ActionTypes.add) {
        initialValues = {
            Name: '',
            FirmEmail: '',
            IsTokenAPIFirm: false,
            IsCollateral: false,
            IsSCFirm: false,
            IsIBNEnabled: false,
            IsLiquidityFacility: false,
            Lei: '',
            Notes: '',
            CompanyCode: undefined,
            APISecondaryConnectionType: undefined,
            Country: undefined,
            State: undefined,
            AssetMapping: []
        };
    } else if (actionType === ActionTypes.edit) {
        if (!editFirm) return <div />;

        const editFirmLocation = editFirm as FredFirmRecord;
        const editFirmCountryOption = getCountryOptions()
            .find((option) => option.value === editFirmLocation?.CountryCode);
        const editFirmStateOption = getStateProvinceOptions(editFirmLocation?.CountryCode)
            .find((option) => option.value === editFirmLocation?.StateCode);

        initialValues = {
            Name: editFirm.Name,
            FirmEmail: editFirm.Email,
            IsTokenAPIFirm: editFirm.IsTokenAPIFirm,
            IsCollateral: (editFirm as FredFirmRecord).IsCollateral,
            IsSCFirm: editFirm.IsSCFirm,
            IsIBNEnabled: editFirm.IsIBNEnabled,
            IsLiquidityFacility: (editFirm as FredFirmRecord).IsLiquidityFacility,
            Lei: (editFirm as FredFirmRecord).Lei,
            Notes: editFirm.Notes,
            CompanyCode: editFirm.CompanyCode,
            // TODO Update in the future to handle multiple API connections
            APISecondaryConnectionType: features?.fireblocks.api && firmSecondaryApiConnections?.length ?
                firmSecondaryApiConnections.some(api => api.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS)
                : undefined,
            Country: editFirmCountryOption,
            State: editFirmStateOption,
            AssetMapping: firmAssetMapping
        };
    }

    const firmOriginalValidations = (values: IFormValues) => validateFirmForm(values, {
        actionType,
        initialValues,
        firms,
        firmUsers,
        firmEoaAccounts,
        userAccounts,
        features,
        getCollateral,
        availableAssets,
        firmAllApiTypeUsers
    });

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                xs={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        await submitFirmForm(values, {
                            actionType,
                            firmId: editFirm ? editFirm.FirmId : '',
                            editFirm: editFirm || null,
                            onClose,
                            setSubmitError,
                            features,
                            initialValues
                        });
                    }}
                    validate={firmOriginalValidations}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentProps}
                            actionType={actionType}
                            availableAssetsMapped={availableAssetsMapped}
                            classes={classes}
                            editFirm={editFirm}
                            features={features}
                            firmAssetMapping={firmAssetMapping}
                            firmAssetStats={firmAssetStats}
                            firmEoaAccounts={firmEoaAccounts}
                            formProps={formProps}
                            getCollateral={getCollateral}
                            getTransactions={getTransactions}
                            secondaryApiConnectionExists={secondaryApiConnectionExists}
                            submitError={submitError}
                            updateFirm={updateFirm}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}